import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Row, Col, Button, Spinner, Form, Badge, Alert } from 'react-bootstrap';
import { FaShieldAlt, FaSave, FaCheckSquare, FaRegSquare, FaBuilding, FaUserTag } from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '../../../api/apiClient';
import {
  getPanelTypeForRole,
  getMenuItemsForRole,
} from '../../../config/menuConfig';

const PANEL_LABELS = {
  user:       { label: 'User Panel',       badge: 'success' },
  org:        { label: 'Org Admin Panel',             badge: 'warning' },
  instructor: { label: 'Instructor Panel',            badge: 'danger'  },
  admin:      { label: 'Super Admin Panel',           badge: 'primary' },
};

const AccessControl = () => {
  const [organizations, setOrganizations] = useState([]);
  const [allRoles, setAllRoles]           = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [selectedRoleValue, setSelectedRoleValue] = useState('');
  const [menuIds, setMenuIds]             = useState([]);
  const [loadingRoles, setLoadingRoles]   = useState(false);
  const [loadingAccess, setLoadingAccess] = useState(false);
  const [saving, setSaving]               = useState(false);
  const [orgRoleFilter, setOrgRoleFilter] = useState([]);

  useEffect(() => {
    api.organizations.getAll()
      .then((res) => {
        const list = res.data?.data || res.data || [];
        setOrganizations(Array.isArray(list) ? list : []);
      })
      .catch(() => setOrganizations([]));
  }, []);

  useEffect(() => {
    setLoadingRoles(true);
    api.accessControl.getAll()
      .then((res) => {
        if (res.data?.success) setAllRoles(res.data.data);
      })
      .catch(() => toast.error('Failed to load roles.'))
      .finally(() => setLoadingRoles(false));
  }, []);

  useEffect(() => {
    if (!selectedOrgId) {
      setOrgRoleFilter([]);
      setSelectedRoleValue('');
      return;
    }
    Promise.all([
      api.organizations.getUsers(selectedOrgId).catch(() => ({ data: [] })),
      api.organizations.getAdmins(selectedOrgId).catch(() => ({ data: [] })),
    ]).then(([usersRes, adminsRes]) => {
      const usersData  = usersRes?.data?.data  || usersRes?.data  || [];
      const adminsData = adminsRes?.data?.data || adminsRes?.data || [];
      const roleValues = [...usersData, ...adminsData]
        .map((u) => parseInt(u.role))
        .filter((v) => !Number.isNaN(v) && v !== 0);
      setOrgRoleFilter([...new Set(roleValues)]);
    }).catch(() => setOrgRoleFilter([]));
    setSelectedRoleValue('');
  }, [selectedOrgId]);

  useEffect(() => {
    if (selectedRoleValue === '') {
      setMenuIds([]);
      return;
    }
    setLoadingAccess(true);
    api.accessControl.getByRole(selectedRoleValue)
      .then((res) => {
        if (res.data?.success) {
          const { full_access, menu_ids } = res.data.data;
          if (full_access) {
            const all = getMenuItemsForRole(selectedRoleValue).map((m) => m.id);
            setMenuIds(all);
          } else {
            setMenuIds(menu_ids ?? []);
          }
        }
      })
      .catch(() => toast.error('Failed to load access config.'))
      .finally(() => setLoadingAccess(false));
  }, [selectedRoleValue]);

  const panelType    = selectedRoleValue !== '' ? getPanelTypeForRole(selectedRoleValue) : null;
  const menuItems    = selectedRoleValue !== '' ? getMenuItemsForRole(selectedRoleValue) : [];
  const panelMeta    = panelType ? PANEL_LABELS[panelType] : null;

  const selectedRole = useMemo(
    () => allRoles.find((r) => r.role_value === parseInt(selectedRoleValue)),
    [allRoles, selectedRoleValue]
  );

  const isOrgAdminRole = useCallback((role) => {
    const value = parseInt(role.role_value, 10);
    if (value === 2) return true;
    return (role.role_name || '').toLowerCase().includes('org admin');
  }, []);

  const isRoleOptionDisabled = useCallback(
    (role) => !selectedOrgId && isOrgAdminRole(role),
    [selectedOrgId, isOrgAdminRole]
  );

  const visibleRoles = useMemo(() => {
    if (!selectedOrgId || orgRoleFilter.length === 0) return allRoles;
    return allRoles.filter((r) => orgRoleFilter.includes(r.role_value));
  }, [allRoles, selectedOrgId, orgRoleFilter]);

  const isAllowed = useCallback((id) => menuIds.includes(id), [menuIds]);

  const toggleMenu = useCallback((id) => {
    setMenuIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }, []);

  const handleSelectAll = useCallback((select) => {
    setMenuIds(select ? menuItems.map((m) => m.id) : []);
  }, [menuItems]);

  const handleSave = useCallback(async () => {
    if (!selectedRoleValue) return;
    try {
      setSaving(true);
      await api.accessControl.upsert(selectedRoleValue, menuIds);
      toast.success(`Access for "${selectedRole?.role_name}" saved successfully.`);
    } catch (error) {
      toast.error('Failed to save: ' + (error.response?.data?.message || error.message));
    } finally {
      setSaving(false);
    }
  }, [selectedRoleValue, menuIds, selectedRole]);

  const standaloneItems  = menuItems.filter((m) => !m.group);
  const masterSetupItems = menuItems.filter((m) => m.group === 'Master Setup');
  const isAdminPanel     = panelType === 'admin';
  const allSelected      = menuItems.length > 0 && menuIds.length === menuItems.length;

  return (
    <div className="p-3 sm:p-4 md:p-6 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 min-h-screen">
      <div className="max-w-5xl mx-auto w-full">

      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <h5 className="fw-bold mb-0 text-xl sm:text-2xl md:text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">
          Role Based Access Control
        </h5>
      </div>

      {/* Filters */}
      <Row className="mb-3 g-2 sm:g-3 align-items-end justify-content-center">
        <Col xs={12} md={5}>
          <label className="text-xs font-semibold text-purple-700 mb-1 d-flex align-items-center gap-1">
            <FaBuilding className="text-purple-600" />
            Organization
            <span className="text-muted fw-normal">(optional)</span>
          </label>
          <Form.Select
            value={selectedOrgId}
            onChange={(e) => setSelectedOrgId(e.target.value)}
            className="rounded-lg sm:rounded-xl shadow-sm border-purple-200 focus:ring-2 focus:ring-purple-500 text-xs sm:text-sm"
          >
            <option value="">All Organizations</option>
            {organizations.map((org) => (
              <option key={org.id} value={org.id}>{org.name}</option>
            ))}
          </Form.Select>
        </Col>

        <Col xs={12} md={5}>
          <label className="text-xs font-semibold text-purple-700 mb-1 d-flex align-items-center gap-1">
            <FaUserTag className="text-purple-600" />
            Role
            <span className="text-danger">*</span>
          </label>
          {loadingRoles ? (
            <div className="d-flex align-items-center gap-2 text-sm text-muted py-2">
              <Spinner animation="border" size="sm" className="text-purple-600" />
              Loading roles…
            </div>
          ) : (
            <Form.Select
              value={selectedRoleValue}
              onChange={(e) => setSelectedRoleValue(e.target.value)}
              className="rounded-lg sm:rounded-xl shadow-sm border-purple-200 focus:ring-2 focus:ring-purple-500 text-xs sm:text-sm"
            >
              <option value="">Select a Role</option>
              {visibleRoles.map((r) => (
                <option
                  key={r.role_value}
                  value={r.role_value}
                  disabled={isRoleOptionDisabled(r)}
                >
                  {r.role_name}
                </option>
              ))}
            </Form.Select>
          )}
        </Col>

        <Col xs={12} md={2}>
          <Button
            bsPrefix="custom-hamburger"
            disabled={!selectedRoleValue || saving || loadingAccess}
            onClick={handleSave}
            className="w-100 border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full d-flex align-items-center justify-content-center gap-2 p-2"
          >
            {saving
              ? <><Spinner animation="border" size="sm" /> Saving…</>
              : <><FaSave className="w-4 h-4" /> Save</>}
          </Button>
        </Col>
      </Row>

      {/* Empty state */}
      {!selectedRoleValue && (
        <Alert variant="info" className="text-center py-6 sm:py-8 rounded-xl sm:rounded-2xl bg-white/80 backdrop-blur shadow-md border border-purple-100">
          <FaShieldAlt size={36} className="text-purple-400 text-center mx-auto mb-2" />
          <p className="text-sm sm:text-base font-medium text-purple-700 mb-0">
            Select a role above to configure its menu access. Super Admin always has full access.
          </p>
        </Alert>
      )}

      {/* Loading */}
      {selectedRoleValue && loadingAccess && (
        <div className="text-center py-16">
          <Spinner animation="border" className="w-12 h-12 text-purple-600" />
        </div>
      )}

      {/* Menu permissions */}
      {selectedRoleValue && !loadingAccess && panelMeta && (
        <div className="overflow-hidden rounded-xl sm:rounded-2xl shadow-lg bg-white">

          {/* Card header — matches table headers across admin modules */}
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 px-3 sm:px-4 py-3 d-flex flex-wrap align-items-center justify-content-between gap-2">
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <FaShieldAlt className="text-white" />
              <span className="text-white fw-bold text-sm sm:text-base">{selectedRole?.role_name}</span>
              <Badge bg="light" text="dark" className="text-xs">{panelMeta.label}</Badge>
              <Badge bg="light" text="dark" className="text-xs">
                {menuIds.length}/{menuItems.length} menus enabled
              </Badge>
            </div>
            <Button
              bsPrefix="custom-hamburger"
              size="sm"
              onClick={() => handleSelectAll(!allSelected)}
              className="border border-white/50 bg-white/20 text-white font-semibold rounded-lg shadow-sm hover:bg-white/30 transition-all duration-200 text-xs d-flex align-items-center gap-1 px-3 py-1"
            >
              {allSelected ? <FaRegSquare /> : <FaCheckSquare />}
              {allSelected ? 'Deselect All' : 'Select All'}
            </Button>
          </div>

          <div className="p-3 sm:p-4">
            {isAdminPanel ? (
              <>
                <div className="mb-4">
                  <div className="row g-2">
                    {standaloneItems.map((menu) => (
                      <Col key={menu.id} xs={12} sm={6} md={4} lg={3} xl={2}>
                        <MenuCheckbox
                          menu={menu}
                          allowed={isAllowed(menu.id)}
                          onToggle={toggleMenu}
                        />
                      </Col>
                    ))}
                  </div>
                </div>

                {masterSetupItems.length > 0 && (
                  <div>
                    <p className="text-xs fw-semibold text-purple-600 text-uppercase mb-2">
                      Master Setup
                    </p>
                    <div className="row g-2">
                      {masterSetupItems.map((menu) => (
                        <Col key={menu.id} xs={12} sm={6} md={4} lg={3} xl={2}>
                          <MenuCheckbox
                            menu={menu}
                            allowed={isAllowed(menu.id)}
                            onToggle={toggleMenu}
                          />
                        </Col>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="row g-2">
                {menuItems.map((menu) => (
                  <Col key={menu.id} xs={12} sm={6} md={4} lg={3} xl={2}>
                    <MenuCheckbox
                      menu={menu}
                      allowed={isAllowed(menu.id)}
                      onToggle={toggleMenu}
                    />
                  </Col>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

const MenuCheckbox = ({ menu, allowed, onToggle }) => (
  <label
    className={`d-flex align-items-center gap-2 px-3 py-2 rounded-lg sm:rounded-xl cursor-pointer border text-xs sm:text-sm transition-all user-select-none h-100
      ${allowed
        ? 'bg-purple-50 border-purple-400 text-purple-800 fw-medium'
        : 'bg-gray-50 border-purple-100 text-gray-600 hover:border-purple-300 hover:bg-purple-50/50'
      }`}
  >
    <Form.Check
      type="checkbox"
      checked={allowed}
      onChange={() => onToggle(menu.id)}
      className="m-0"
    />
    <span>{menu.label}</span>
  </label>
);

export default AccessControl;
